import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getDatabase, ref as dbRef, set, push } from 'firebase/database';
import { Attachment, ImageAttachment, FileAttachment } from '../types/attachments';
import { softDeleteFile } from '../api/fileDeletionApi';
import { recordActivity } from '../api/activityApi';
import { formatDateOnlyForStorage, formatDateForStorage } from './DateUtils';
import { getImageDimensions } from './ImageUploadUtils';
import { isImageFile } from './FileUtils';

export interface ProjectAttachmentUploadResult {
    success: boolean;
    attachment?: Attachment;
    error?: string;
}

/**
 * Upload a file attachment to a project
 */
export const uploadProjectAttachment = async (
    buildingKey: string,
    projectKey: string,
    file: File,
    uploadedBy: string,
    displayName?: string
): Promise<ProjectAttachmentUploadResult> => {
    try {
        const db = getDatabase();
        const storage = getStorage();

        // Generate unique attachment key using Firebase push
        // Database uses flat structure: projects/{projectKey}
        const attachmentsRef = dbRef(db, `projects/${projectKey}/attachments`);
        const newAttachmentRef = push(attachmentsRef);
        const attachmentKey = newAttachmentRef.key!;

        // Storage retains buildingKey for file organization
        const storagePath = `projects/${buildingKey}/${projectKey}/attachments/${attachmentKey}`;

        // Upload file to Storage
        const fileRef = storageRef(storage, storagePath);
        await uploadBytes(fileRef, file);

        // Get file extension
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

        // Check if image and get dimensions if so
        const isImage = isImageFile(file);

        let attachment: Attachment;

        if (isImage) {
            // For images, use PhotoModel structure (can reuse existing image components)
            try {
                const dimensions = await getImageDimensions(file);

                const imageAttachment: ImageAttachment = {
                    key: attachmentKey,
                    author: uploadedBy,
                    date: formatDateForStorage(new Date()), // PhotoModel uses YYYY-MM-DD hh:mm:ss format
                    width: dimensions.width,
                    height: dimensions.height,
                    storagePath: storagePath,
                    fileSize: file.size,
                    attachmentType: 'image',
                    fileName: file.name,
                    mimeType: file.type,
                    ...(displayName && { displayName })
                };

                attachment = imageAttachment;
            } catch (error) {
                console.warn('Failed to get image dimensions:', error);
                // Fallback: create image attachment with default dimensions
                const imageAttachment: ImageAttachment = {
                    key: attachmentKey,
                    author: uploadedBy,
                    date: formatDateForStorage(new Date()), // PhotoModel uses YYYY-MM-DD hh:mm:ss format
                    width: 0,
                    height: 0,
                    storagePath: storagePath,
                    fileSize: file.size,
                    attachmentType: 'image',
                    fileName: file.name,
                    mimeType: file.type,
                    ...(displayName && { displayName })
                };
                attachment = imageAttachment;
            }
        } else {
            // For non-image files, use FileAttachment
            const fileAttachment: FileAttachment = {
                key: attachmentKey,
                fileName: file.name,
                fileType: fileExtension,
                mimeType: file.type,
                storagePath: storagePath,
                fileSize: file.size,
                uploadedBy: uploadedBy,
                uploadedAt: formatDateOnlyForStorage(new Date()),
                attachmentType: 'file',
                ...(displayName && { displayName })
            };

            attachment = fileAttachment;
        }

        // Save attachment metadata to RTDB
        await set(newAttachmentRef, attachment);
        recordActivity('document.uploaded', { buildingKey, targetKey: projectKey });

        return {
            success: true,
            attachment
        };
    } catch (error) {
        console.error('Error uploading project attachment:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error occurred'
        };
    }
};

/**
 * Delete a project attachment (soft delete — moves the metadata node to a
 * tombstone at deletedFiles/{key}; the Storage blob is left for a future
 * cleanup job)
 */
export const deleteProjectAttachment = async (
    buildingKey: string,
    projectKey: string,
    attachmentKey: string
): Promise<{ success: boolean; error?: string }> => {
    try {
        // Database uses flat structure: projects/{projectKey}
        await softDeleteFile({
            originalPath: `projects/${projectKey}/attachments/${attachmentKey}`,
            entityType: 'projectAttachment',
        });

        return {
            success: true
        };
    } catch (error) {
        console.error('Error deleting project attachment:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error occurred'
        };
    }
};

/**
 * Get download URL for an attachment
 */
export const getProjectAttachmentDownloadUrl = async (
    storagePath: string
): Promise<string> => {
    try {
        const storage = getStorage();
        const fileRef = storageRef(storage, storagePath);
        return await getDownloadURL(fileRef);
    } catch (error) {
        console.error('Error getting attachment download URL:', error);
        throw error;
    }
};
